import {
	META_WHATSAPP_BASE,
	authHeaders,
} from 'api/metaWhatsappApi';

/**
 * Existing duplicate route.
 */
export async function duplicateCampaign({ id }) {
	if (!id) {
		throw new Error('A campaign id is required.');
	}

	const headers = new Headers(authHeaders());
	headers.set('Content-Type', 'application/json');

	let response;

	try {
		response = await fetch(
			`${META_WHATSAPP_BASE}/campaigns/${encodeURIComponent(id)}/duplicate`,
			{
				method: 'POST',
				headers,
				body: JSON.stringify({}),
			},
		);
	} catch (cause) {
		const error = new Error(
			'Could not confirm the new draft. Refresh the list before trying again.',
		);

		error.status = 'FETCH_ERROR';
		error.cause = cause;

		throw error;
	}

	const data = await response.json().catch(() => ({}));

	if (!response.ok) {
		const error = new Error(
			data?.message ||
				'Could not create the new draft.',
		);

		error.status = response.status;
		error.data = data;

		throw error;
	}

	return data;
}

/**
 * Delete an unstarted campaign.
 */
export async function deleteCampaign({
	id,
	reason,
}) {
	if (!id) {
		throw new Error('A campaign id is required.');
	}

	const headers = new Headers(authHeaders());

	headers.set(
		'Content-Type',
		'application/json',
	);

	let response;

	try {
		response = await fetch(
			`${META_WHATSAPP_BASE}/campaigns/${encodeURIComponent(id)}`,
			{
				method: 'DELETE',
				headers,
				body: JSON.stringify(
					reason
						? {
								reason,
							}
						: {},
				),
			},
		);
	} catch (cause) {
		const error = new Error(
			'Could not confirm deletion. Check the campaign status before trying another action.',
		);

		error.status = 'FETCH_ERROR';
		error.cause = cause;

		throw error;
	}

	const data = await response
		.json()
		.catch(() => null);

	if (!response.ok) {
		const error = new Error(
			data?.message ||
				'Could not delete the campaign.',
		);

		error.status = response.status;
		error.data = data;

		throw error;
	}

	/**
	 * Never hide a campaign simply because an HTTP request returned 200.
	 * Verify the server explicitly confirms this exact campaign.
	 */
	if (
		data?.deleted !== true ||
		String(data.campaignId ?? '') !==
			String(id)
	) {
		const error = new Error(
			'The deletion response could not be verified.',
		);

		error.status = 'PARSING_ERROR';

		throw error;
	}

	return data;
}

/**
 * When DELETE's network response is interrupted, query the exact campaign
 * before permitting another action.
 */
export async function readCampaignAfterDelete(id) {
	if (!id) {
		throw new Error('A campaign id is required.');
	}

	let response;

	try {
		response = await fetch(
			`${META_WHATSAPP_BASE}/campaigns/${encodeURIComponent(id)}`,
			{
				method: 'GET',
				headers: new Headers(
					authHeaders(),
				),
				cache: 'no-store',
			},
		);
	} catch (cause) {
		const error = new Error(
			'Could not check the campaign status.',
		);

		error.status = 'FETCH_ERROR';
		error.cause = cause;

		throw error;
	}

	const data = await response
		.json()
		.catch(() => null);

	if (
		response.status === 404 &&
		data?.error === 'CAMPAIGN_NOT_FOUND'
	) {
		return {
			missing: true,
		};
	}

	const campaign =
		data?.campaign ??
		data?.data?.campaign;

	if (
		response.ok &&
		campaign &&
		String(
			campaign._id ??
				campaign.id ??
				'',
		) === String(id)
	) {
		return {
			missing: false,
			campaign,
		};
	}

	const error = new Error(
		data?.message ||
			'The campaign status could not be verified.',
	);

	error.status = response.ok
		? 'PARSING_ERROR'
		: response.status;

	error.data = data;

	throw error;
}